<template>
  <div class="admin-dashboard">
    <div class="admin-sidebar">
      <div class="admin-logo">
        <h2>幸福输入法</h2>
        <div class="admin-badge">管理</div>
      </div>
      
      <nav class="admin-nav">
        <ul>
          <li class="active">
            <a href="#" @click.prevent="setActiveTab('dashboard')">
              <span class="nav-icon">📊</span> 概览
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="setActiveTab('users')">
              <span class="nav-icon">👥</span> 用户管理
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="setActiveTab('skins')">
              <span class="nav-icon">🎨</span> 皮肤管理
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="setActiveTab('wallpapers')">
              <span class="nav-icon">🖼️</span> 壁纸管理
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="setActiveTab('announcements')">
              <span class="nav-icon">📢</span> 公告管理
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="setActiveTab('messages')">
              <span class="nav-icon">💬</span> 消息广播
            </a>
          </li>
          <li>
            <a href="/keyboard-tool" target="_blank">
              <span class="nav-icon">⌨️</span> 键盘工具
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="setActiveTab('settings')">
              <span class="nav-icon">⚙️</span> 系统设置
            </a>
          </li>
        </ul>
      </nav>
      
      <div class="admin-logout">
        <button @click="handleLogout" class="logout-btn">
          <span class="nav-icon">🚪</span> 退出登录
        </button>
      </div>
    </div>
    
    <div class="admin-content">
      <div class="admin-header">
        <h1>{{ pageTitle }}</h1>
        <div class="admin-user">
          <span class="user-greeting">欢迎，管理员</span>
        </div>
      </div>
      
      <div class="content-wrapper">
        <!-- 仪表盘概览 -->
        <div v-if="activeTab === 'dashboard'" class="dashboard-overview">
          <div class="dashboard-cards">
            <div class="stat-card">
              <div class="stat-icon">👥</div>
              <div class="stat-info">
                <h3>总用户</h3>
                <p class="stat-value" v-if="!isLoading.userStats">{{ userStats.totalUsers.toLocaleString() }}</p>
                <p class="stat-value skeleton" v-else>-</p>
                <p class="stat-change positive">新增 {{ userStats.newUsersLast7Days }} <span>最近7天</span></p>
              </div>
            </div>
            
            <div class="stat-card">
              <div class="stat-icon">📱</div>
              <div class="stat-info">
                <h3>活跃用户</h3>
                <p class="stat-value" v-if="!isLoading.userStats">{{ userStats.activeUsers.toLocaleString() }}</p>
                <p class="stat-value skeleton" v-else>-</p>
                <p class="stat-change positive">当前在线 {{ userStats.onlineUsers }} 人</p>
              </div>
            </div>
            
            <div class="stat-card">
              <div class="stat-icon">🚀</div>
              <div class="stat-info">
                <h3>活跃率</h3>
                <p class="stat-value" v-if="!isLoading.userStats">{{ calculateActiveRate() }}%</p>
                <p class="stat-value skeleton" v-else>-</p>
                <p class="stat-change positive">活跃/总用户 比率</p>
              </div>
            </div>
            
            <div class="stat-card">
              <div class="stat-icon">⭐</div>
              <div class="stat-info">
                <h3>使用中皮肤</h3>
                <p class="stat-value" v-if="!isLoading.skinUsage">{{ calculateTotalSkinUsage() }}</p>
                <p class="stat-value skeleton" v-else>-</p>
                <p class="stat-change neutral">{{ skinUsage.length }} 种皮肤</p>
              </div>
            </div>
          </div>
          
          <div class="dashboard-section">
            <h2 class="section-title">皮肤使用情况</h2>
            <div v-if="isLoading.skinUsage" class="loading-placeholder">
              <p>加载皮肤使用数据中...</p>
            </div>
            <div v-else class="skin-usage-chart">
              <div class="chart-container">
                <div class="bar-chart">
                  <div v-for="(skin, index) in skinUsage" :key="skin.skinId" class="bar-item">
                    <div class="bar-label">{{ skin.skinName }}</div>
                    <div class="bar-container">
                      <div class="bar" :style="{ width: calculateBarWidth(skin.userCount), backgroundColor: getBarColor(index) }"></div>
                      <div class="bar-value">{{ skin.userCount }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="skin-stats">
                <div class="skin-stat-item" v-for="(skin, index) in skinUsage" :key="'stat-'+skin.skinId">
                  <div class="skin-stat-color" :style="{ backgroundColor: getBarColor(index) }"></div>
                  <div class="skin-stat-name">{{ skin.skinName }}</div>
                  <div class="skin-stat-value">{{ ((skin.userCount / calculateTotalSkinUsage()) * 100).toFixed(1) }}%</div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="dashboard-section">
            <h2 class="section-title">用户活跃度趋势</h2>
            <div class="chart-placeholder">
              <p>用户活跃度趋势图表开发中...</p>
            </div>
          </div>
        </div>
        
        <!-- 皮肤管理标签页 -->
        <div v-if="activeTab === 'skins'" class="skins-management">
          <div class="dashboard-section">
            <h2 class="section-title">皮肤列表</h2>
            <div class="skins-list">
              <div v-if="isLoading.skinUsage" class="loading-placeholder">
                <p>加载皮肤数据中...</p>
              </div>
              <table v-else class="data-table">
                <thead>
                  <tr>
                    <th>皮肤ID</th>
                    <th>皮肤名称</th>
                    <th>使用人数</th>
                    <th>使用占比</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="skin in skinUsage" :key="skin.skinId">
                    <td>{{ skin.skinId }}</td>
                    <td>{{ skin.skinName }}</td>
                    <td>{{ skin.userCount }}</td>
                    <td>{{ ((skin.userCount / calculateTotalSkinUsage()) * 100).toFixed(1) }}%</td>
                    <td>
                      <button class="action-btn">详情</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div class="dashboard-section">
            <h2 class="section-title">皮肤详细统计</h2>
            <div class="skins-detailed-stats">
              <div v-if="isLoading.skinDetailedStats" class="loading-placeholder">
                <p>加载皮肤详细统计数据中...</p>
              </div>
              <table v-else class="data-table">
                <thead>
                  <tr>
                    <th>皮肤ID</th>
                    <th>皮肤名称</th>
                    <th>描述</th>
                    <th>价格</th>
                    <th>总用户数</th>
                    <th>付费购买数</th>
                    <th>兑换码购买数</th>
                    <th>总收入</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="stat in skinDetailedStats" :key="stat.id">
                    <td>{{ stat.skinId || '默认' }}</td>
                    <td>{{ stat.name }}</td>
                    <td>{{ stat.description }}</td>
                    <td>¥{{ stat.price.toFixed(2) }}</td>
                    <td>{{ stat.totalUsers }}</td>
                    <td>{{ stat.paidPurchaseCount }}</td>
                    <td>{{ stat.redeemCodePurchaseCount }}</td>
                    <td>¥{{ stat.totalRevenue.toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <!-- 壁纸管理标签页 -->
        <div v-if="activeTab === 'wallpapers'" class="wallpapers-management">
          <div class="dashboard-section">
            <h2 class="section-title">壁纸上传</h2>
            <div class="wallpaper-upload-section">
              <div class="upload-area" 
                   :class="{ 'drag-over': isDragOver }"
                   @drop="handleDrop"
                   @dragover="handleDragOver"
                   @dragleave="handleDragLeave"
                   @click="openFileDialog">
                <input type="file" 
                       ref="fileInput" 
                       @change="handleFileSelect" 
                       accept="image/*" 
                       multiple 
                       style="display: none;">
                <div class="upload-icon">📸</div>
                <div class="upload-text">
                  <p>点击上传或拖拽图片文件到此处</p>
                  <p class="upload-hint">支持 JPG, PNG, WebP 格式，单个文件不超过 5MB</p>
                </div>
              </div>
              
              <!-- 预览区域 -->
              <div v-if="selectedFiles.length > 0" class="preview-section">
                <h3>选中的文件 ({{ selectedFiles.length }})</h3>
                <div class="file-preview-grid">
                  <div v-for="(file, index) in selectedFiles" :key="index" class="file-preview-item">
                    <div class="preview-image" v-if="file.preview">
                      <img :src="file.preview" :alt="file.name" />
                    </div>
                    <div class="file-info">
                      <div class="file-name">{{ file.name }}</div>
                      <div class="file-size">{{ formatFileSize(file.size) }}</div>
                    </div>
                    <button @click="removeFile(index)" class="remove-file-btn">×</button>
                  </div>
                </div>
                
                <!-- 上传按钮 -->
                <div class="upload-actions">
                  <button @click="uploadWallpapers" 
                          :disabled="isUploading" 
                          class="upload-btn">
                    <span v-if="!isUploading">上传壁纸</span>
                    <span v-else>上传中... {{ uploadProgress }}%</span>
                  </button>
                  <button @click="clearFiles" class="clear-btn">清空</button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 已上传壁纸列表 -->
          <div class="dashboard-section">
            <h2 class="section-title">壁纸列表</h2>
            <div class="wallpapers-list">
              <div v-if="isLoading.wallpapers" class="loading-placeholder">
                <p>加载壁纸列表中...</p>
              </div>
              <div v-else-if="wallpapers.length === 0" class="empty-state">
                <p>暂无壁纸，请先上传</p>
              </div>
              <div v-else class="wallpaper-grid">
                <div v-for="wallpaper in wallpapers" :key="wallpaper.id" class="wallpaper-item">
                  <div class="wallpaper-thumbnail">
                    <img :src="wallpaper.thumbnailUrl || wallpaper.url" :alt="wallpaper.name" />
                  </div>
                  <div class="wallpaper-info">
                    <div class="wallpaper-name">{{ wallpaper.name }}</div>
                    <div class="wallpaper-meta">
                      <span class="wallpaper-size">{{ wallpaper.width }}×{{ wallpaper.height }}</span>
                      <span class="wallpaper-date">{{ formatDate(wallpaper.createdAt) }}</span>
                    </div>
                  </div>
                  <div class="wallpaper-actions">
                    <button @click="viewWallpaper(wallpaper)" class="action-btn view-btn">预览</button>
                    <button @click="deleteWallpaper(wallpaper.id)" class="action-btn delete-btn">删除</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 公告管理标签页 -->
        <div v-if="activeTab === 'announcements'" class="announcements-management">
          <div class="dashboard-section">
            <h2 class="section-title">发布公告</h2>
            <div class="announcement-form">
              <!-- 公告标题 -->
              <div class="form-group">
                <label class="form-label">公告标题</label>
                <input 
                  type="text" 
                  v-model="announcementForm.title" 
                  placeholder="请输入公告标题" 
                  class="form-input"
                  maxlength="100">
                <div class="char-count">{{ announcementForm.title.length }}/100</div>
              </div>

              <!-- 公告内容 -->
              <div class="form-group">
                <label class="form-label">公告内容</label>
                <textarea 
                  v-model="announcementForm.content" 
                  placeholder="请输入公告内容" 
                  class="form-textarea"
                  rows="6"
                  maxlength="1000"></textarea>
                <div class="char-count">{{ announcementForm.content.length }}/1000</div>
              </div>

              <!-- 跳转链接 -->
              <div class="form-group">
                <label class="form-label">跳转链接 <span class="optional-label">(可选)</span></label>
                <input 
                  type="url" 
                  v-model="announcementForm.redirectUrl" 
                  placeholder="请输入跳转链接，如：https://example.com" 
                  class="form-input">
                <div class="form-hint">用户点击公告时将跳转到此链接，留空则无跳转</div>
              </div>

              <!-- 封面图片上传 -->
              <div class="form-group">
                <label class="form-label">封面图片</label>
                <div class="cover-upload-area" 
                     :class="{ 'drag-over': isCoverDragOver }"
                     @drop="handleCoverDrop"
                     @dragover="handleCoverDragOver"
                     @dragleave="handleCoverDragLeave">
                  <div v-if="!announcementForm.coverPreview" 
                       class="upload-placeholder" 
                       @click="selectCoverImage">
                    <div class="upload-icon">📷</div>
                    <div class="upload-text">
                      <p>点击上传或拖拽图片到此处</p>
                      <p class="upload-hint">支持 JPG, PNG 格式，建议尺寸 16:9，最大 2MB</p>
                    </div>
                  </div>
                  <div v-else class="cover-preview">
                    <img :src="announcementForm.coverPreview" alt="封面预览" />
                    <div class="cover-actions">
                      <button @click="selectCoverImage" class="change-btn">更换</button>
                      <button @click="removeCoverImage" class="remove-btn">删除</button>
                    </div>
                  </div>
                  <input 
                    type="file" 
                    ref="coverInput" 
                    @change="handleCoverImageSelect" 
                    accept="image/*" 
                    style="display: none;">
                </div>
              </div>

              <!-- 公告设置 -->
              <div class="form-group">
                <div class="form-row">
                  <div class="form-col">
                    <label class="form-label">优先级</label>
                    <select v-model="announcementForm.priority" class="form-select">
                      <option value="0">普通</option>
                      <option value="1">重要</option>
                      <option value="2">紧急</option>
                      <option value="3">置顶</option>
                    </select>
                  </div>
                  <div class="form-col">
                    <label class="form-label">状态</label>
                    <div class="form-switch">
                      <input 
                        type="checkbox" 
                        id="enabledSwitch" 
                        v-model="announcementForm.enabled">
                      <label for="enabledSwitch" class="switch-label">
                        {{ announcementForm.enabled ? '启用' : '禁用' }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 提交按钮 -->
              <div class="form-actions">
                <button 
                  @click="publishAnnouncement" 
                  :disabled="isPublishing" 
                  class="publish-btn">
                  <span v-if="!isPublishing">发布公告</span>
                  <span v-else>发布中...</span>
                </button>
                <button @click="resetForm" class="reset-btn">重置</button>
              </div>
            </div>
          </div>

          <!-- 已发布公告列表 -->
          <div class="dashboard-section">
            <h2 class="section-title">公告列表</h2>
            <div class="announcements-list">
              <div v-if="isLoading.announcements" class="loading-placeholder">
                <p>加载公告列表中...</p>
              </div>
              <div v-else-if="announcements.length === 0" class="empty-state">
                <p>暂无公告</p>
              </div>
              <div v-else class="announcements-table">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>封面</th>
                      <th>标题</th>
                      <th>内容预览</th>
                      <th>优先级</th>
                      <th>状态</th>
                      <th>发布时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="announcement in announcements" :key="announcement.id">
                      <td>
                        <div class="announcement-cover">
                          <img v-if="announcement.coverImage" 
                               :src="announcement.coverImage" 
                               :alt="announcement.title" />
                          <div v-else class="no-cover">无封面</div>
                        </div>
                      </td>
                      <td class="announcement-title">{{ announcement.title }}</td>
                      <td class="announcement-content">{{ truncateText(announcement.content, 50) }}</td>
                      <td>
                        <span :class="['priority-badge', getPriorityClass(announcement.priority)]">
                          {{ getPriorityText(announcement.priority) }}
                        </span>
                      </td>
                      <td>
                        <span :class="['status-badge', announcement.enabled ? 'enabled' : 'disabled']">
                          {{ announcement.enabled ? '启用' : '禁用' }}
                        </span>
                      </td>
                      <td>{{ formatDate(announcement.createdAt) }}</td>
                      <td>
                        <div class="table-actions">
                          <button @click="editAnnouncement(announcement)" class="action-btn edit-btn">编辑</button>
                          <button @click="deleteAnnouncement(announcement.id)" class="action-btn delete-btn">删除</button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- 消息广播标签页 -->
        <div v-if="activeTab === 'messages'" class="messages-management">
          <div class="dashboard-section">
            <h2 class="section-title">发送消息广播</h2>
            <div class="message-form">
              <!-- 消息标题 -->
              <div class="form-group">
                <label class="form-label">消息标题</label>
                <input 
                  type="text" 
                  v-model="messageForm.title" 
                  placeholder="请输入消息标题" 
                  class="form-input"
                  maxlength="100">
                <div class="char-count">{{ messageForm.title.length }}/100</div>
              </div>

              <!-- 消息内容 -->
              <div class="form-group">
                <label class="form-label">消息内容</label>
                <textarea 
                  v-model="messageForm.content" 
                  placeholder="请输入消息内容" 
                  class="form-textarea"
                  rows="8"
                  maxlength="2000"></textarea>
                <div class="char-count">{{ messageForm.content.length }}/2000</div>
              </div>

              <!-- 消息设置 -->
              <div class="form-group">
                <div class="form-row">
                  <div class="form-col">
                    <label class="form-label">消息类型</label>
                    <select v-model="messageForm.messageType" class="form-select">
                      <option value="SYSTEM">系统消息</option>
                      <option value="MAINTENANCE">维护通知</option>
                      <option value="UPDATE">更新提醒</option>
                      <option value="PROMOTION">促销活动</option>
                    </select>
                  </div>
                  <div class="form-col">
                    <label class="form-label">过期时间 <span class="optional-label">(可选)</span></label>
                    <input 
                      type="datetime-local" 
                      v-model="messageForm.expiryTime" 
                      class="form-input">
                  </div>
                </div>
              </div>

              <!-- 操作链接 -->
              <div class="form-group">
                <label class="form-label">操作链接 <span class="optional-label">(可选)</span></label>
                <input 
                  type="text" 
                  v-model="messageForm.actionUrl" 
                  placeholder="请输入操作链接，如：/maintenance-details" 
                  class="form-input">
                <div class="form-hint">用户点击消息时的跳转路径，留空则无跳转</div>
              </div>

              <!-- 提交按钮 -->
              <div class="form-actions">
                <button 
                  @click="broadcastMessage" 
                  :disabled="isBroadcasting" 
                  class="broadcast-btn">
                  <span v-if="!isBroadcasting">发送广播</span>
                  <span v-else>发送中...</span>
                </button>
                <button @click="resetMessageForm" class="reset-btn">重置</button>
              </div>
            </div>
          </div>

          <!-- 已发送消息列表 -->
          <div class="dashboard-section">
            <h2 class="section-title">消息记录</h2>
            <div class="messages-list">
              <div v-if="isLoading.messages" class="loading-placeholder">
                <p>加载消息记录中...</p>
              </div>
              <div v-else-if="messages.length === 0" class="empty-state">
                <p>暂无消息记录</p>
              </div>
              <div v-else class="messages-table">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>标题</th>
                      <th>内容预览</th>
                      <th>消息类型</th>
                      <th>过期时间</th>
                      <th>发送时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="message in messages" :key="message.id">
                      <td class="message-title">{{ message.title }}</td>
                      <td class="message-content">{{ truncateText(message.content, 50) }}</td>
                      <td>
                        <span :class="['message-type-badge', getMessageTypeClass(message.messageType)]">
                          {{ getMessageTypeText(message.messageType) }}
                        </span>
                      </td>
                      <td>{{ message.expiryTime ? formatDateTime(message.expiryTime) : '永久有效' }}</td>
                      <td>{{ formatDateTime(message.createdAt) }}</td>
                      <td>
                        <div class="table-actions">
                          <button @click="viewMessage(message)" class="action-btn view-btn">查看</button>
                          <button @click="deleteMessage(message.id)" class="action-btn delete-btn">删除</button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 其他标签页占位符 -->
        <div v-if="activeTab !== 'dashboard' && activeTab !== 'skins' && activeTab !== 'wallpapers' && activeTab !== 'announcements' && activeTab !== 'messages'" class="placeholder-content">
          <p>{{ pageTitle }}功能正在开发中，敬请期待...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadToOSS } from '@/config/oss'
import { createApiUrl, createRequestOptions, API_CONFIG } from '@/config/api'

export default {
  name: 'AdminDashboard',
  data() {
    return {
      activeTab: 'dashboard',
      userStats: {
        totalUsers: 0,
        onlineUsers: 0,
        activeUsers: 0,
        newUsersLast7Days: 0
      },
      skinUsage: [],
      skinDetailedStats: [],
      wallpapers: [],
      selectedFiles: [],
      isDragOver: false,
      isUploading: false,
      uploadProgress: 0,
      // 公告相关数据
      announcements: [],
      isPublishing: false,
      isCoverDragOver: false,
      // 消息广播相关数据
      messages: [],
      isBroadcasting: false,
      messageForm: {
        title: '',
        content: '',
        messageType: 'SYSTEM',
        expiryTime: '',
        actionUrl: ''
      },
      announcementForm: {
        title: '',
        content: '',
        redirectUrl: '',
        coverImage: '',
        coverPreview: '',
        coverFile: null,
        enabled: true,
        priority: 0
      },
      isLoading: {
        userStats: true,
        skinUsage: true,
        skinDetailedStats: true,
        wallpapers: false,
        announcements: false,
        messages: false
      }
    }
  },
  computed: {
    pageTitle() {
      const titles = {
        dashboard: '仪表盘概览',
        users: '用户管理',
        skins: '皮肤管理',
        wallpapers: '壁纸管理',
        announcements: '公告管理',
        messages: '消息广播',
        settings: '系统设置'
      }
      return titles[this.activeTab] || '管理后台'
    }
  },
  mounted() {
    this.fetchUserStatistics();
    this.fetchSkinUsage();
    this.fetchSkinDetailedStatistics();
  },
      watch: {
    activeTab(newTab) {
      if (newTab === 'wallpapers' && this.wallpapers.length === 0) {
        this.fetchWallpapers();
      }
      if (newTab === 'announcements' && this.announcements.length === 0) {
        this.fetchAnnouncements();
      }
      if (newTab === 'messages' && this.messages.length === 0) {
        this.fetchMessages();
      }
    }
  },
  methods: {
    setActiveTab(tab) {
      this.activeTab = tab
    },
    handleLogout() {
      localStorage.removeItem('admin_token')
      this.$router.push({ name: 'AdminLogin' })
    },
    async fetchUserStatistics() {
      this.isLoading.userStats = true;
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.users.statistics),
          createRequestOptions()
        );
        if (response.ok) {
          this.userStats = await response.json();
        } else {
          console.error('获取用户统计失败:', response.statusText);
        }
      } catch (error) {
        console.error('获取用户统计出错:', error);
      } finally {
        this.isLoading.userStats = false;
      }
    },
    async fetchSkinUsage() {
      this.isLoading.skinUsage = true;
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.skins.usage),
          createRequestOptions()
        );
        if (response.ok) {
          this.skinUsage = await response.json();
        } else {
          console.error('获取皮肤使用统计失败:', response.statusText);
        }
      } catch (error) {
        console.error('获取皮肤使用统计出错:', error);
      } finally {
        this.isLoading.skinUsage = false;
      }
    },
    calculateActiveRate() {
      if (this.userStats.totalUsers === 0) return 0;
      return ((this.userStats.activeUsers / this.userStats.totalUsers) * 100).toFixed(2);
    },
    calculateTotalSkinUsage() {
      return this.skinUsage.reduce((total, skin) => total + skin.userCount, 0);
    },
    calculateBarWidth(userCount) {
      const maxWidth = 200;
      const maxUserCount = this.calculateTotalSkinUsage();
      return `${(userCount / maxUserCount) * maxWidth}px`;
    },
    getBarColor(index) {
      const colors = ['#FF6B8B', '#FF9A9E', '#FFC796', '#FFD3B6'];
      return colors[index % colors.length];
    },
    async fetchSkinDetailedStatistics() {
      this.isLoading.skinDetailedStats = true;
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.skins.detailed),
          createRequestOptions()
        );
        if (response.ok) {
          this.skinDetailedStats = await response.json();
        } else {
          console.error('获取皮肤详细统计失败:', response.statusText);
        }
      } catch (error) {
        console.error('获取皮肤详细统计出错:', error);
      } finally {
        this.isLoading.skinDetailedStats = false;
      }
    },
    
    // 壁纸相关方法
    async fetchWallpapers() {
      this.isLoading.wallpapers = true;
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.wallpapers.list),
          createRequestOptions()
        );
        if (response.ok) {
          this.wallpapers = await response.json();
        } else {
          console.error('获取壁纸列表失败:', response.statusText);
        }
      } catch (error) {
        console.error('获取壁纸列表出错:', error);
      } finally {
        this.isLoading.wallpapers = false;
      }
    },
    
    openFileDialog() {
      this.$refs.fileInput.click();
    },
    
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
    },
    
    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },
    
    handleDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },
    
    handleDragLeave(event) {
      event.preventDefault();
      this.isDragOver = false;
    },
    
    processFiles(files) {
      const validFiles = files.filter(file => {
        // 检查文件类型
        if (!file.type.startsWith('image/')) {
          alert(`文件 ${file.name} 不是图片格式`);
          return false;
        }
        // 检查文件大小（5MB限制）
        if (file.size > 5 * 1024 * 1024) {
          alert(`文件 ${file.name} 超过5MB大小限制`);
          return false;
        }
        return true;
      });
      
      validFiles.forEach(file => {
        // 创建预览
        const reader = new FileReader();
        reader.onload = (e) => {
          this.selectedFiles.push({
            file: file,
            name: file.name,
            size: file.size,
            preview: e.target.result
          });
        };
        reader.readAsDataURL(file);
      });
    },
    
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    
    clearFiles() {
      this.selectedFiles = [];
      this.$refs.fileInput.value = '';
    },
    
    async uploadWallpapers() {
      if (this.selectedFiles.length === 0) {
        alert('请先选择文件');
        return;
      }
      
      this.isUploading = true;
      this.uploadProgress = 0;
      
      try {
        for (let i = 0; i < this.selectedFiles.length; i++) {
          const fileData = this.selectedFiles[i];
          const formData = new FormData();
          formData.append('wallpaper', fileData.file);
          formData.append('name', fileData.name);
          
          const response = await fetch(
            createApiUrl(API_CONFIG.endpoints.wallpapers.upload),
            createRequestOptions({
              method: 'POST',
              body: formData,
              headers: {} // FormData需要浏览器自动设置Content-Type
            })
          );
          
          if (!response.ok) {
            throw new Error(`上传 ${fileData.name} 失败: ${response.statusText}`);
          }
          
          this.uploadProgress = Math.round(((i + 1) / this.selectedFiles.length) * 100);
        }
        
        // 上传成功
        alert('壁纸上传成功！');
        this.clearFiles();
        this.fetchWallpapers(); // 重新获取壁纸列表
        
      } catch (error) {
        console.error('上传出错:', error);
        alert('上传失败: ' + error.message);
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
      }
    },
    
    async deleteWallpaper(wallpaperId) {
      if (!confirm('确定要删除这张壁纸吗？')) {
        return;
      }
      
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.wallpapers.delete(wallpaperId)),
          createRequestOptions({
            method: 'DELETE'
          })
        );
        
        if (response.ok) {
          alert('壁纸删除成功');
          this.fetchWallpapers(); // 重新获取壁纸列表
        } else {
          throw new Error('删除失败: ' + response.statusText);
        }
      } catch (error) {
        console.error('删除出错:', error);
        alert('删除失败: ' + error.message);
      }
    },
    
    viewWallpaper(wallpaper) {
      // 在新窗口中预览壁纸
      window.open(wallpaper.url, '_blank');
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    
    // 公告相关方法
    async fetchAnnouncements() {
      this.isLoading.announcements = true;
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.announcements.list),
          createRequestOptions()
        );
        if (response.ok) {
          this.announcements = await response.json();
        } else {
          console.error('获取公告列表失败:', response.statusText);
        }
      } catch (error) {
        console.error('获取公告列表出错:', error);
      } finally {
        this.isLoading.announcements = false;
      }
    },
    
    selectCoverImage() {
      this.$refs.coverInput.click();
    },
    
         handleCoverImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.processCoverImage(file);
    },

    handleCoverDrop(event) {
      event.preventDefault();
      this.isCoverDragOver = false;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processCoverImage(files[0]);
      }
    },

    handleCoverDragOver(event) {
      event.preventDefault();
      this.isCoverDragOver = true;
    },

    handleCoverDragLeave(event) {
      event.preventDefault();
      this.isCoverDragOver = false;
    },

    processCoverImage(file) {
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件');
        return;
      }
      
      // 验证文件大小（限制为2MB）
      if (file.size > 2 * 1024 * 1024) {
        alert('图片文件不能超过2MB');
        return;
      }
      
      // 保存文件对象
      this.announcementForm.coverFile = file;
      
      // 创建预览
      const reader = new FileReader();
      reader.onload = (e) => {
        this.announcementForm.coverPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    removeCoverImage() {
      this.announcementForm.coverFile = null;
      this.announcementForm.coverPreview = '';
      this.announcementForm.coverImage = '';
      this.$refs.coverInput.value = '';
    },
    
    async publishAnnouncement() {
      // 验证表单
      if (!this.announcementForm.title.trim()) {
        alert('请输入公告标题');
        return;
      }
      
      if (!this.announcementForm.content.trim()) {
        alert('请输入公告内容');
        return;
      }
      
      this.isPublishing = true;
      
      try {
        let coverImageUrl = '';
        
        // 如果有封面图片，先上传到OSS
        if (this.announcementForm.coverFile) {
          console.log('开始上传封面图片到OSS...');
          const uploadResult = await uploadToOSS(this.announcementForm.coverFile, 'announcements');
          
          if (uploadResult.success) {
            coverImageUrl = uploadResult.name; // 使用相对路径
            console.log('封面图片上传成功:', coverImageUrl);
          } else {
            throw new Error('封面图片上传失败: ' + uploadResult.error);
          }
        }
        
        // 发布公告
        const announcementData = {
          title: this.announcementForm.title.trim(),
          content: this.announcementForm.content.trim(),
          coverImage: coverImageUrl,
          redirectUrl: this.announcementForm.redirectUrl.trim() || null,
          enabled: this.announcementForm.enabled,
          priority: parseInt(this.announcementForm.priority)
        };
        
        console.log('发布公告数据:', announcementData);
        
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.announcements.create),
          createRequestOptions({
            method: 'POST',
            body: JSON.stringify(announcementData)
          })
        );
        
        if (response.ok) {
          alert('公告发布成功！');
          this.resetForm();
          this.fetchAnnouncements(); // 重新获取公告列表
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || '发布失败');
        }
        
      } catch (error) {
        console.error('发布公告出错:', error);
        alert('发布失败: ' + error.message);
      } finally {
        this.isPublishing = false;
      }
    },
    
    resetForm() {
      this.announcementForm = {
        title: '',
        content: '',
        redirectUrl: '',
        coverImage: '',
        coverPreview: '',
        coverFile: null,
        enabled: true,
        priority: 0
      };
      this.isCoverDragOver = false;
      if (this.$refs.coverInput) {
        this.$refs.coverInput.value = '';
      }
    },
    
    async deleteAnnouncement(announcementId) {
      if (!confirm('确定要删除这条公告吗？')) {
        return;
      }
      
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.announcements.delete(announcementId)),
          createRequestOptions({
            method: 'DELETE'
          })
        );
        
        if (response.ok) {
          alert('公告删除成功');
          this.fetchAnnouncements(); // 重新获取公告列表
        } else {
          throw new Error('删除失败: ' + response.statusText);
        }
      } catch (error) {
        console.error('删除公告出错:', error);
        alert('删除失败: ' + error.message);
      }
    },
    
    editAnnouncement() {
      // 编辑功能（可以扩展）
      alert('编辑功能开发中...');
    },
    
    truncateText(text, maxLength) {
      if (text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + '...';
    },
    
    getPriorityText(priority) {
      const priorityMap = {
        0: '普通',
        1: '重要',
        2: '紧急',
        3: '置顶'
      };
      return priorityMap[priority] || '普通';
    },
    
    getPriorityClass(priority) {
      const classMap = {
        0: 'normal',
        1: 'important',
        2: 'urgent',
        3: 'top'
      };
      return classMap[priority] || 'normal';
    },
    
    // 消息广播相关方法
    async fetchMessages() {
      this.isLoading.messages = true;
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.messages.list),
          createRequestOptions()
        );
        if (response.ok) {
          this.messages = await response.json();
        } else {
          console.error('获取消息列表失败:', response.statusText);
        }
      } catch (error) {
        console.error('获取消息列表出错:', error);
      } finally {
        this.isLoading.messages = false;
      }
    },
    
    async broadcastMessage() {
      // 验证表单
      if (!this.messageForm.title.trim()) {
        alert('请输入消息标题');
        return;
      }
      
      if (!this.messageForm.content.trim()) {
        alert('请输入消息内容');
        return;
      }
      
      this.isBroadcasting = true;
      
      try {
        // 准备消息数据
        const messageData = {
          title: this.messageForm.title.trim(),
          content: this.messageForm.content.trim(),
          messageType: this.messageForm.messageType,
          expiryTime: this.messageForm.expiryTime || "",
          actionUrl: this.messageForm.actionUrl.trim() || ""
        };
        
        console.log('发送消息广播数据:', messageData);
        
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.messages.create),
          createRequestOptions({
            method: 'POST',
            body: JSON.stringify(messageData)
          })
        );
        
        if (response.ok) {
          alert('消息广播发送成功！');
          this.resetMessageForm();
          this.fetchMessages(); // 重新获取消息列表
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || '发送失败');
        }
        
      } catch (error) {
        console.error('发送消息广播出错:', error);
        alert('发送失败: ' + error.message);
      } finally {
        this.isBroadcasting = false;
      }
    },
    
    resetMessageForm() {
      this.messageForm = {
        title: '',
        content: '',
        messageType: 'SYSTEM',
        expiryTime: '',
        actionUrl: ''
      };
    },
    
    async deleteMessage(messageId) {
      if (!confirm('确定要删除这条消息记录吗？')) {
        return;
      }
      
      try {
        const response = await fetch(
          createApiUrl(API_CONFIG.endpoints.messages.delete(messageId)),
          createRequestOptions({
            method: 'DELETE'
          })
        );
        
        if (response.ok) {
          alert('消息删除成功');
          this.fetchMessages(); // 重新获取消息列表
        } else {
          throw new Error('删除失败: ' + response.statusText);
        }
      } catch (error) {
        console.error('删除消息出错:', error);
        alert('删除失败: ' + error.message);
      }
    },
    
    viewMessage(message) {
      // 查看消息详情
      const messageDetails = `标题：${message.title}\n\n内容：${message.content}\n\n类型：${this.getMessageTypeText(message.messageType)}\n\n过期时间：${message.expiryTime || '永久有效'}\n\n操作链接：${message.actionUrl || '无'}`;
      alert(messageDetails);
    },
    
    getMessageTypeText(messageType) {
      const typeMap = {
        'SYSTEM': '系统消息',
        'MAINTENANCE': '维护通知',
        'UPDATE': '更新提醒',
        'PROMOTION': '促销活动'
      };
      return typeMap[messageType] || '系统消息';
    },
    
    getMessageTypeClass(messageType) {
      const classMap = {
        'SYSTEM': 'system',
        'MAINTENANCE': 'maintenance',
        'UPDATE': 'update',
        'PROMOTION': 'promotion'
      };
      return classMap[messageType] || 'system';
    },
    
    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
}
</script>

<style scoped>
@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.admin-dashboard {
  display: flex;
  min-height: 100vh;
}

.admin-sidebar {
  width: 250px;
  background-color: #2A0E55;
  color: white;
  display: flex;
  flex-direction: column;
  padding: 20px 0;
}

.admin-logo {
  padding: 0 20px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.admin-logo h2 {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

.admin-badge {
  background: #FF6B8B;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
}

.admin-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.admin-nav li {
  margin-bottom: 2px;
}

.admin-nav a {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s;
}

.admin-nav a:hover, .admin-nav li.active a {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-icon {
  margin-right: 10px;
  font-size: 18px;
}

.admin-logout {
  margin-top: auto;
  padding: 0 20px;
}

.logout-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
}

.logout-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.admin-content {
  flex: 1;
  background-color: #f9fafc;
  overflow-y: auto;
}

.admin-header {
  background-color: white;
  padding: 20px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.admin-header h1 {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #333;
}

.user-greeting {
  font-size: 14px;
  color: #666;
}

.content-wrapper {
  padding: 30px;
}

.dashboard-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
}

.stat-icon {
  font-size: 30px;
  margin-right: 15px;
}

.stat-info {
  flex: 1;
}

.stat-info h3 {
  font-size: 14px;
  color: #666;
  margin: 0 0 5px 0;
  font-weight: 600;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 5px 0;
}

.stat-change {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
}

.stat-change span {
  font-weight: normal;
  color: #666;
}

.positive {
  color: #10b981;
}

.negative {
  color: #ef4444;
}

.neutral {
  color: #6b7280;
}

.dashboard-section {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 20px 0;
  color: #333;
}

.placeholder-chart {
  height: 200px;
  background-color: #f5f7fb;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.feedback-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.feedback-item {
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
}

.feedback-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.feedback-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.user-name {
  font-weight: 600;
  color: #333;
}

.feedback-date {
  font-size: 12px;
  color: #666;
  margin-left: 10px;
}

.feedback-content {
  margin: 0;
  color: #444;
  line-height: 1.5;
}

.placeholder-content {
  background-color: white;
  border-radius: 10px;
  padding: 50px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-align: center;
  color: #666;
}

.loading-placeholder {
  height: 200px;
  background-color: #f5f7fb;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.skin-usage-chart {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.chart-container {
  flex: 1;
  background-color: white;
  border-radius: 5px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
}

.bar-item {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  height: 30px;
}

.bar-label {
  width: 120px;
  font-size: 14px;
  font-weight: 500;
  color: #444;
  margin-right: 15px;
}

.bar-container {
  flex: 1;
  height: 20px;
  background-color: #f5f7fb;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.bar {
  height: 100%;
  background-color: #FF6B8B;
  border-radius: 10px;
  transition: width 0.5s ease;
}

.bar-value {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  font-size: 12px;
  color: white;
}

.skin-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.skin-stat-item {
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: #f9fafc;
  border-radius: 8px;
}

.skin-stat-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}

.skin-stat-name {
  font-size: 13px;
  color: #555;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.skin-stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-left: auto;
}

.chart-placeholder {
  height: 150px;
  background-color: #f5f7fb;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  height: 24px;
  width: 80px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th, .data-table td {
  padding: 10px;
  text-align: left;
}

.data-table th {
  background-color: #f5f7fb;
}

.data-table tbody tr:nth-child(even) {
  background-color: #f9fafc;
}

.action-btn {
  padding: 8px 16px;
  background-color: #FF6B8B;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
}

.action-btn:hover {
  background-color: #FF9A9E;
}

.skins-detailed-stats {
  margin-top: 10px;
}

/* 壁纸上传相关样式 */
.wallpaper-upload-section {
  margin-bottom: 30px;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f9fafb;
}

.upload-area:hover {
  border-color: #FF6B8B;
  background-color: #fef2f2;
}

.upload-area.drag-over {
  border-color: #FF6B8B;
  background-color: #fef2f2;
  transform: scale(1.02);
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.upload-text p {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #374151;
}

.upload-hint {
  font-size: 14px !important;
  color: #6b7280 !important;
}

.preview-section {
  margin-top: 30px;
}

.preview-section h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

.file-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.file-preview-item {
  position: relative;
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-image {
  width: 100%;
  height: 120px;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 10px;
  background-color: #f5f5f5;
}

.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-info {
  text-align: left;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.file-size {
  font-size: 12px;
  color: #666;
}

.remove-file-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.remove-file-btn:hover {
  background-color: #dc2626;
  transform: scale(1.1);
}

.upload-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-start;
}

.upload-btn {
  padding: 12px 24px;
  background-color: #FF6B8B;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.upload-btn:hover:not(:disabled) {
  background-color: #FF9A9E;
  transform: translateY(-1px);
}

.upload-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clear-btn {
  padding: 12px 24px;
  background-color: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.clear-btn:hover {
  background-color: #e5e7eb;
}

.wallpapers-list {
  margin-top: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.wallpaper-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.wallpaper-item {
  background-color: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.wallpaper-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.wallpaper-thumbnail {
  width: 100%;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  background-color: #f5f5f5;
}

.wallpaper-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wallpaper-info {
  margin-bottom: 12px;
}

.wallpaper-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wallpaper-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
}

.wallpaper-actions {
  display: flex;
  gap: 8px;
}

.view-btn {
  background-color: #3b82f6;
}

.view-btn:hover {
  background-color: #2563eb;
}

.delete-btn {
  background-color: #ef4444;
}

.delete-btn:hover {
  background-color: #dc2626;
}

/* 公告管理相关样式 */
.announcements-management {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.announcement-form {
  max-width: 800px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.optional-label {
  font-weight: 400;
  color: #9ca3af;
  font-size: 12px;
}

.form-hint {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-input:focus {
  outline: none;
  border-color: #FF6B8B;
  box-shadow: 0 0 0 3px rgba(255, 107, 139, 0.1);
}

.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s;
}

.form-textarea:focus {
  outline: none;
  border-color: #FF6B8B;
  box-shadow: 0 0 0 3px rgba(255, 107, 139, 0.1);
}

.char-count {
  font-size: 12px;
  color: #6b7280;
  text-align: right;
  margin-top: 4px;
}

.cover-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  overflow: hidden;
  background-color: #fafafa;
  transition: all 0.3s;
}

.cover-upload-area.drag-over {
  border-color: #FF6B8B;
  background-color: #fef2f2;
  transform: scale(1.01);
}

.upload-placeholder {
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.upload-placeholder:hover {
  background-color: #f0f9ff;
  border-color: #FF6B8B;
}

.cover-preview {
  position: relative;
  display: inline-block;
  width: 100%;
}

.cover-preview img {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  display: block;
}

.cover-actions {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
}

.change-btn, .remove-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.change-btn {
  background-color: #3b82f6;
  color: white;
}

.change-btn:hover {
  background-color: #2563eb;
}

.remove-btn {
  background-color: #ef4444;
  color: white;
}

.remove-btn:hover {
  background-color: #dc2626;
}

.form-row {
  display: flex;
  gap: 20px;
}

.form-col {
  flex: 1;
}

.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
}

.form-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-switch input[type="checkbox"] {
  display: none;
}

.switch-label {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  background-color: #ccc;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.switch-label::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s;
}

.form-switch input[type="checkbox"]:checked + .switch-label {
  background-color: #FF6B8B;
}

.form-switch input[type="checkbox"]:checked + .switch-label::after {
  transform: translateX(26px);
}

.form-actions {
  display: flex;
  gap: 15px;
  margin-top: 30px;
}

.publish-btn {
  padding: 12px 30px;
  background-color: #FF6B8B;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.publish-btn:hover:not(:disabled) {
  background-color: #FF9A9E;
  transform: translateY(-1px);
}

.publish-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.reset-btn {
  padding: 12px 30px;
  background-color: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.reset-btn:hover {
  background-color: #e5e7eb;
}

.announcements-table {
  overflow-x: auto;
}

.announcement-cover {
  width: 60px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.announcement-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-cover {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6b7280;
  background-color: #f9fafb;
}

.announcement-title {
  font-weight: 600;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.announcement-content {
  max-width: 300px;
  color: #6b7280;
  font-size: 14px;
}

.priority-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.priority-badge.normal {
  background-color: #6b7280;
}

.priority-badge.important {
  background-color: #f59e0b;
}

.priority-badge.urgent {
  background-color: #ef4444;
}

.priority-badge.top {
  background-color: #8b5cf6;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.status-badge.enabled {
  background-color: #10b981;
}

.status-badge.disabled {
  background-color: #6b7280;
}

.table-actions {
  display: flex;
  gap: 8px;
}

.edit-btn {
  background-color: #3b82f6;
  font-size: 12px;
  padding: 6px 12px;
}

.edit-btn:hover {
  background-color: #2563eb;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .announcements-table {
    font-size: 12px;
  }
  
  .announcement-title {
    max-width: 120px;
  }
  
  .announcement-content {
    max-width: 150px;
  }
}

/* 消息广播相关样式 */
.messages-management {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.message-form {
  max-width: 800px;
}

.broadcast-btn {
  padding: 12px 30px;
  background-color: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.broadcast-btn:hover:not(:disabled) {
  background-color: #059669;
  transform: translateY(-1px);
}

.broadcast-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.messages-table {
  overflow-x: auto;
}

.message-title {
  font-weight: 600;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-content {
  max-width: 300px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.4;
}

.message-type-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.message-type-badge.system {
  background-color: #3b82f6;
}

.message-type-badge.maintenance {
  background-color: #f59e0b;
}

.message-type-badge.update {
  background-color: #10b981;
}

.message-type-badge.promotion {
  background-color: #ec4899;
}

@media (max-width: 768px) {
  .messages-table {
    font-size: 12px;
  }
  
  .message-title {
    max-width: 120px;
  }
  
  .message-content {
    max-width: 150px;
  }
}

/* Toast 提示样式 */
.toast {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  z-index: 10000;
  transform: translateX(100%);
  opacity: 0;
  transition: all 0.3s ease;
  max-width: 300px;
  word-wrap: break-word;
}

.toast.show {
  transform: translateX(0);
  opacity: 1;
}

.toast.toast-success {
  background-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

.toast.toast-error {
  background-color: #ef4444;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
}

.toast.toast-info {
  background-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}
</style> 